import { useCallback, useEffect, useState } from "react";
import type { StackFrame } from "./parseStackFrames";

interface StackFrameListProps {
  frames: StackFrame[];
  /** Called when a frame is clicked, or Enter'd with keyboardNavigation on. */
  onSelectFrame: (frame: StackFrame) => void;
  /** Uppercase heading above the list. Omit for no heading. */
  title?: string;
  /** Footer line telling the user what clicking a frame does. */
  hint?: string;
  /**
   * Arrow-key navigation with Enter to open, plus a highlighted row. Off by
   * default: it installs a document-level keydown listener, which is a real
   * behaviour change for a boundary that didn't have one.
   */
  keyboardNavigation?: boolean;
}

/**
 * A clickable list of stack frames, for an error boundary's developer view.
 * Renders nothing when there are no frames — which is the normal production
 * case, since parseStackFrames drops minified frames.
 *
 * One look, built from theme tokens (border / muted / content-*), all of which
 * are defined in both `:root` and `.dark`. So a caller on a dark error surface
 * puts this inside a `dark` scope and the list follows; there's no light/dark
 * prop, and no opacity-based colours (see TextColorRubric).
 */
export function StackFrameList(props: StackFrameListProps) {
  const {
    frames,
    onSelectFrame,
    title,
    hint,
    keyboardNavigation = false,
  } = props;
  const [selected, setSelected] = useState(0);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (frames.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((s) => Math.min(s + 1, frames.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((s) => Math.max(s - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const frame = frames[selected];
        if (frame) onSelectFrame(frame);
      }
    },
    [frames, selected, onSelectFrame],
  );

  useEffect(() => {
    if (!keyboardNavigation) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [keyboardNavigation, handleKeyDown]);

  if (frames.length === 0) {
    return <></>;
  }

  return (
    <div>
      {title !== undefined && (
        <div className="mb-1.5 text-[10px] font-bold tracking-widest text-content-muted uppercase">
          {title}
        </div>
      )}
      <div className="overflow-hidden rounded-lg border border-border">
        {frames.map((frame, i) => (
          <button
            key={`${frame.file}:${frame.line}:${frame.column}`}
            type="button"
            onClick={() => onSelectFrame(frame)}
            onMouseEnter={keyboardNavigation ? () => setSelected(i) : undefined}
            className={`flex w-full items-baseline gap-3 border-b border-border px-3 py-2 text-left font-mono text-xs transition-colors last:border-b-0 ${
              keyboardNavigation && i === selected
                ? "bg-muted text-foreground"
                : "text-content-secondary hover:bg-muted"
            }`}
          >
            <span className="shrink-0 font-semibold text-foreground">
              {frame.name}
            </span>
            <span className="truncate text-content-muted">
              {frame.file}:{frame.line}
            </span>
          </button>
        ))}
      </div>
      {hint !== undefined && (
        <div className="mt-1 px-3 text-[10px] text-content-faint">{hint}</div>
      )}
    </div>
  );
}
